import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  CalendarDays,
  Clock,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  Truck,
  User as UserIcon,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { AppShell } from '@/components/layout/app-shell'
import {
  PlanningDialog,
} from '@/components/planning/planning-dialog'
import {
  PlanningFilters,
  computeRange,
  type PlanningFiltersValue,
} from '@/components/planning/planning-filters'
import type {
  ClientLite,
  DriverLite,
  PlanningRow,
  VehicleLite,
} from '@/components/planning/planning-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { ApiError } from '@/lib/api'

/**
 * Planning — the main daily-operations page.
 *
 * Organised top-to-bottom:
 *   1. Page title + "+ Nouveau" button
 *   2. Filter bar (period preset, driver, search)
 *   3. Grouped list of plannings:
 *        - Today
 *        - Tomorrow
 *        - Then one date per day until the end of the range
 *
 * Every row carries a per-row dropdown ("⋮") with Edit / Delete
 * actions. Edit opens the same dialog as "+ Nouveau" pre-populated;
 * Delete pops a confirmation dialog.
 *
 * State management
 * ----------------
 * The filter values live in local state (NOT URL params yet —
 * we'll lift them to TanStack Router's `validateSearch` once the
 * admin uses bookmarks / share-URLs in anger). The drivers /
 * vehicles / clients lists come from their respective endpoints
 * and are cached for 5 min.
 */

const PLANNING_QUERY_KEY = 'planning' as const

export function PlanningPage() {
  const api = useApi()
  const queryClient = useQueryClient()

  // Filters — defaults to "today".
  const [filters, setFilters] = useState<PlanningFiltersValue>(() => {
    const range = computeRange('today')
    return {
      preset: 'today',
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      driverId: null,
      search: '',
    }
  })

  // Dialog state — `null` = create mode, a row = edit mode,
  // `undefined` = closed.
  const [editing, setEditing] = useState<PlanningRow | null | undefined>(undefined)
  const [pendingDelete, setPendingDelete] = useState<PlanningRow | null>(null)

  /* -------- list query -------- */

  const planningQuery = useQuery({
    queryKey: [
      PLANNING_QUERY_KEY,
      'list',
      filters.dateFrom,
      filters.dateTo,
      filters.driverId ?? '',
      filters.search,
    ] as const,
    queryFn: () => {
      const params: Record<string, string | number | boolean> = {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        // The API caps `limit` at 100 (see `planning.schema.ts`).
        // Combined with the date-range filter, 100 rows easily
        // covers a week of work for 25 drivers; if the customer
        // picks "Cette semaine" + everything is full we'll need
        // proper paging or a higher cap.
        limit: 100,
        sortBy: 'date',
        sortOrder: 'asc',
      }
      if (filters.driverId !== null) params.driverId = filters.driverId
      if (filters.search.trim() !== '') params.search = filters.search.trim()
      return api.getPaginated<PlanningRow>('/api/planning', params)
    },
  })

  /* -------- reference data for selects + filters -------- */

  const driversQuery = useQuery({
    queryKey: ['users', 'drivers'] as const,
    queryFn: () =>
      api.get<DriverLite[]>('/api/users', { role: 'driver', active: true, limit: 100 }),
    staleTime: 5 * 60 * 1000,
  })
  const vehiclesQuery = useQuery({
    queryKey: ['vehicles', 'lite'] as const,
    queryFn: () =>
      api.get<VehicleLite[]>('/api/vehicles', {
        limit: 100,
        sortBy: 'registration',
        sortOrder: 'asc',
      }),
    staleTime: 5 * 60 * 1000,
  })
  const clientsQuery = useQuery({
    queryKey: ['clients', 'lite'] as const,
    queryFn: () =>
      api.get<ClientLite[]>('/api/clients', {
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      }),
    staleTime: 5 * 60 * 1000,
  })

  /* -------- delete mutation -------- */

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/planning/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [PLANNING_QUERY_KEY] })
      setPendingDelete(null)
    },
  })

  /* -------- group rows by date -------- */

  const grouped = useMemo(
    () => groupByDate(planningQuery.data?.data ?? []),
    [planningQuery.data],
  )

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text">Planning</h1>
          <p className="text-sm text-text-muted">
            Organisez les missions à venir de vos chauffeurs.
          </p>
        </div>
        <Button onClick={() => setEditing(null)}>
          <Plus size={16} />
          Nouveau planning
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent>
          <PlanningFilters
            value={filters}
            onChange={setFilters}
            drivers={driversQuery.data ?? []}
            driversLoading={driversQuery.isLoading}
          />
        </CardContent>
      </Card>

      {/* List */}
      {planningQuery.isLoading ? (
        <div className="py-16 flex justify-center">
          <Spinner size={28} label="Chargement du planning" />
        </div>
      ) : planningQuery.isError ? (
        <Card>
          <CardContent>
            <p className="text-danger">
              Impossible de charger le planning. Vérifiez votre connexion.
            </p>
          </CardContent>
        </Card>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-text-muted">
            <CalendarDays size={28} className="mx-auto mb-2 opacity-60" aria-hidden="true" />
            <p>Aucun planning sur cette période.</p>
            <Button
              variant="secondary"
              className="mt-4"
              onClick={() => setEditing(null)}
            >
              Créer un planning
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-6">
          {grouped.map((group) => (
            <section key={group.date}>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-2">
                {formatGroupDate(group.date)} ·{' '}
                <span className="font-normal normal-case">
                  {group.rows.length} planning{group.rows.length > 1 ? 's' : ''}
                </span>
              </h2>
              <Card>
                <ul className="divide-y divide-surface-border">
                  {group.rows.map((row) => (
                    <PlanningListRow
                      key={row.id}
                      row={row}
                      clientColor={
                        clientsQuery.data?.find((c) => c.id === row.clientId)?.color ??
                        null
                      }
                      onEdit={() => setEditing(row)}
                      onDelete={() => setPendingDelete(row)}
                    />
                  ))}
                </ul>
              </Card>
            </section>
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <PlanningDialog
        planning={editing}
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined)
        }}
        drivers={driversQuery.data ?? []}
        vehicles={vehiclesQuery.data ?? []}
        clients={clientsQuery.data ?? []}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Supprimer le planning&nbsp;?</DialogTitle>
            <DialogDescription>
              Cette action est définitive. Le planning ne pourra plus être
              consulté.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {pendingDelete !== null ? (
              <div className="text-sm text-text">
                <p className="font-medium">{pendingDelete.site}</p>
                <p className="text-text-muted mt-1">
                  {formatGroupDate(pendingDelete.date)} · {pendingDelete.driverName ?? '—'}
                </p>
              </div>
            ) : null}
            {deleteMutation.error !== null ? (
              <p className="mt-3 text-sm text-danger">
                {deleteMutation.error instanceof ApiError
                  ? deleteMutation.error.message
                  : 'Suppression impossible.'}
              </p>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Annuler
              </Button>
            </DialogClose>
            <Button
              variant="danger"
              onClick={() => {
                if (pendingDelete !== null) deleteMutation.mutate(pendingDelete.id)
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Spinner label="Suppression" />
              ) : (
                <>
                  <Trash2 size={16} />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  )
}

/* -------------------------------------------------------------------------- */
/*  Per-row component                                                          */
/* -------------------------------------------------------------------------- */

interface PlanningListRowProps {
  row: PlanningRow
  clientColor: string | null
  onEdit: () => void
  onDelete: () => void
}

function PlanningListRow({ row, clientColor, onEdit, onDelete }: PlanningListRowProps) {
  return (
    <li className="px-4 py-3 flex items-center gap-4 hover:bg-surface-hover/50 transition-colors">
      {/* Client colour pill */}
      <span
        aria-hidden="true"
        className="w-2 h-10 rounded-full shrink-0"
        style={{ background: clientColor ?? '#cbd5e1' }}
      />

      {/* Time / "Journée" */}
      <div className="w-28 shrink-0 text-sm font-medium text-text">
        {row.isDaily ? (
          <Badge tone="template">Journée</Badge>
        ) : row.startTime !== null && row.endTime !== null ? (
          <span className="font-mono tabular-nums">
            {trimSeconds(row.startTime)} – {trimSeconds(row.endTime)}
          </span>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </div>

      {/* Site (primary) */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-text truncate">{row.site}</p>
        <p className="text-xs text-text-muted truncate">
          {[row.driverName, row.vehicleRegistration, row.clientName]
            .filter((s) => s !== null && s !== '')
            .join(' · ') || '—'}
        </p>
      </div>

      {/* Quick metadata icons (visible from md up) */}
      <div className="hidden md:flex items-center gap-3 text-xs text-text-muted">
        {row.driverName !== null ? (
          <span className="inline-flex items-center gap-1">
            <UserIcon size={12} aria-hidden="true" /> {row.driverName}
          </span>
        ) : null}
        {row.vehicleRegistration !== null ? (
          <span className="inline-flex items-center gap-1">
            <Truck size={12} aria-hidden="true" /> {row.vehicleRegistration}
          </span>
        ) : null}
        {!row.isDaily && row.startTime !== null && row.endTime !== null ? (
          <span className="inline-flex items-center gap-1">
            <Clock size={12} aria-hidden="true" />
            {trimSeconds(row.startTime)} – {trimSeconds(row.endTime)}
          </span>
        ) : null}
      </div>

      {/* Status placeholder. The hasMission flag isn't on
          /api/planning today — see planning-types.ts note. The
          dropdown next to it is the per-row action menu. */}
      <Badge tone="warning">À remplir</Badge>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Actions"
            className="p-1.5 rounded-md text-text-muted hover:bg-surface-hover hover:text-text"
          >
            <MoreVertical size={16} aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => onEdit()}>
            <Pencil size={14} aria-hidden="true" />
            Modifier
          </DropdownMenuItem>
          <DropdownMenuItem danger onSelect={() => onDelete()}>
            <Trash2 size={14} aria-hidden="true" />
            Supprimer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  )
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

interface DateGroup {
  date: string
  rows: PlanningRow[]
}

function groupByDate(rows: readonly PlanningRow[]): DateGroup[] {
  const map = new Map<string, PlanningRow[]>()
  for (const row of rows) {
    const list = map.get(row.date) ?? []
    list.push(row)
    map.set(row.date, list)
  }
  // Sort by ISO date ascending (already sorted by API but the
  // group order should match regardless of API quirks).
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, rs]) => ({ date, rows: rs }))
}

function formatGroupDate(iso: string): string {
  // "2026-04-21" → "Mardi 21 avril 2026"
  const date = parseISO(iso)
  return capitalize(format(date, 'EEEE d MMMM yyyy', { locale: fr }))
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function trimSeconds(value: string): string {
  return value.length === 8 ? value.slice(0, 5) : value
}
